import { CheckCircleIcon, WarningCircleIcon } from '@phosphor-icons/react';
import { Line, LineChart, ResponsiveContainer, XAxis, YAxis } from 'recharts';

import { cn } from '@shared/lib/utils';
import { ChartContainer, type ChartConfig } from '@shared/ui';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@shared/ui/card';

export interface SectionCardProps {
  title: string;
  description: string;
  score: number;
  trend: number[];
  improveCount?: number;
  color?: string;
  accent?: string;
  className?: string;
  img: string;
  url: string;
}

export const SectionCard = ({
  title,
  description,
  score,
  trend,
  improveCount,
  color = '#155DFC',
  accent = '#e5e7eb',
  className,
  img,
  url,
}: SectionCardProps) => {
  const data = trend.map((value, idx) => ({ value, idx }));

  const chartConfig: ChartConfig = {
    trend: {
      label: 'Trend',
      color,
    },
  };

  const hasImprovements = Boolean(improveCount && improveCount > 0);

  return (
    <a href={url}>
      <Card
        className={cn(
          'h-52 flex-row gap-4 rounded-2xl hover:shadow-md transition-shadow duration-200 ease-out px-4 py-5 ',
          className
        )}
      >
        <div
          className="h-full w-3/10 rounded-xl bg-cover bg-center bg-no-repeat"
          style={{
            backgroundColor: accent,
            backgroundImage: img ? `url(${img})` : undefined,
          }}
        />

        <div className="flex w-7/10 flex-col gap-3">
          <CardHeader className="gap-1 px-0">
            <CardTitle className="text-lg font-medium text-foreground group-hover/card:underline underline-offset-2 decoration-current/50">
              {title}
            </CardTitle>
            <CardDescription className="leading-relaxed">{description}</CardDescription>
          </CardHeader>

          <CardContent className="flex items-center justify-between gap-3 px-0">
            <div className="flex gap-2">
              {hasImprovements ? (
                <>
                  <span className="text-lg text-amber-600">{score}%</span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-1 text-xs text-amber-700">
                    <WarningCircleIcon className="h-3.5 w-3.5" />
                    {improveCount} subskills to improve
                  </span>
                </>
              ) : (
                <span className="inline-flex items-center gap-2 text-lg text-emerald-600">
                  {score}% <CheckCircleIcon className="h-4 w-4" />
                </span>
              )}
            </div>

            <ChartContainer config={chartConfig} className="h-12 w-2/10">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={data} margin={{ top: 8, bottom: 0, left: 0, right: 0 }}>
                  <XAxis dataKey="idx" hide />
                  <YAxis
                    hide
                    domain={[
                      (dataMin: number) => (typeof dataMin === 'number' ? dataMin - 5 : 0),
                      (dataMax: number) => (typeof dataMax === 'number' ? dataMax + 5 : 100),
                    ]}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke={color}
                    strokeWidth={1.5}
                    dot={false}
                    isAnimationActive={true}
                  />
                </LineChart>
              </ResponsiveContainer>
            </ChartContainer>
          </CardContent>
        </div>
      </Card>
    </a>
  );
};
